// Re-Tech homepage "The Bench": finder selection, dated promo strip, mobile dock, doors, links (and the shared
// footer's), reveal + section-view tracking, lazy section modules, and the interactive test bench (HUD, frame-time
// graph, thermal view, teardown callouts, keyboard). GA4 events are consent-gated and sent only from the live theme
// (home_locale on every event).
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const root = $('[data-hb]');
if (root) init();

function init() {
  let data;
  try { data = JSON.parse($('[data-hb-data]', root).textContent); } catch { return; }
  const L = data.locale === 'en' ? 'en' : 'bg';
  const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} }
  };
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  // Prices (decision D6): BG "1 150 €", EN "€1,150"; same rules as snippets/rt-bench-money.liquid
  const group = n => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, L === 'en' ? ',' : ' ');
  const eur = n => {
    const cents = Math.round(n * 100), units = Math.trunc(cents / 100), rem = Math.abs(cents % 100);
    const s = group(units) + (rem ? (L === 'en' ? '.' : ',') + String(rem).padStart(2, '0') : '');
    return L === 'en' ? '€' + s : s + ' €';
  };
  const fmt = (s, o) => s.replace(/\{(\w+)\}/g, (_, k) => o[k] ?? '');
  const icons = {};
  ($('[data-hb-icons]', root)?.content.querySelectorAll('[data-i]') || []).forEach(n => { icons[n.dataset.i] = n.innerHTML; });
  const RES = ['1080p', '1440p', '4K'];
  const AAA = ['warzone', 'cyberpunk-2077', 'rdr2', 'black-myth-wukong', 'cod-black-ops-6'];

  let C;
  try { C = JSON.parse($('[data-hb-copy]', root).textContent); } catch { return; }

  // ---------- GA4 (consent-gated, live theme only) ----------
  const GA = 'G-QR3RPVJ3M1';
  const live = data.mode === 'live';
  const queue = [];
  let gaReady = false;
  const analyticsOk = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  function gaInit() {
    if (gaReady) return;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') window.gtag = function () { window.dataLayer.push(arguments); };
    if (!$('script[src*="googletagmanager.com/gtag/js"]')) {
      const sc = document.createElement('script'); sc.async = true; sc.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA; document.head.append(sc);
      window.gtag('js', new Date());
    }
    window.gtag('config', GA, {send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false});
    gaReady = true;
  }
  function flush() { if (!live || !queue.length || !analyticsOk()) return; gaInit(); while (queue.length) { const [n, p] = queue.shift(); window.gtag('event', n, p); } }
  function track(name, params = {}) {
    const p = {...params, home_locale: L, send_to: GA};
    if (!live) { document.dispatchEvent(new CustomEvent('hv2:preview-event', {detail: {name, params: p}})); return; }
    queue.push([name, p]); flush();
  }
  document.addEventListener('visitorConsentCollected', flush);
  window.addEventListener('pageshow', flush);
  document.addEventListener('rt:header', e => track('home_header', {home_label: e.detail}));
  track('home_view');

  // ---------- GPU data ----------
  const gpus = (data.gpus || []).filter(Boolean);
  // Measured rows override estimates (same rule as product pages). All rows so far are independent reviews.
  const measured = {};
  String(data.bench || '').split(';;').forEach(row => {
    const [id, g, r, fps, src, low] = row.split('~');
    if (!id || !(Number(fps) > 0)) return;
    const k = id + '|' + g + '|' + r;
    if (measured[k] && measured[k].src === 're-tech_test') return;
    measured[k] = {fps: Number(fps), low: Number(low) > 0 ? Number(low) : null, src};
  });
  const srcText = s => s === 'est' ? C.est : C.ind;
  // estimates arrive flat (snippets/rt-bench-perf): [fps, 1% low] per game in data.gk order × RES
  const GI = Object.fromEntries((data.gk || []).map((k, i) => [k, i]));
  // { v: avg FPS, low: 1% low or null, src: 'est' | 'ind' } for one game, or the 5-game AAA average
  function fpsOf(gp, game, res) {
    const m = measured[gp.id + '|' + game + '|' + res] || measured[gp.did + '|' + game + '|' + res];
    if (m) return {v: m.fps, low: m.low, src: 'ind'};
    const i = GI[game], r = RES.indexOf(res);
    if (!gp.p || i == null || r < 0) return null;
    const k = (i * 3 + r) * 2, v = gp.p[k], low = gp.p[k + 1];
    return v > 0 ? {v, low: low > 0 ? low : null, src: 'est'} : null;
  }
  function score(gp, game, res) {
    if (game !== 'aaa') return fpsOf(gp, game, res);
    const rows = AAA.map(g => fpsOf(gp, g, res)).filter(Boolean);
    if (rows.length < 3) return null;
    const avg = k => Math.round(rows.reduce((a, r) => a + r[k], 0) / rows.length);
    return {v: avg('v'), low: rows.every(r => r.low) ? avg('low') : null, src: rows.every(r => r.src === 'ind') ? 'ind' : 'est'};
  }
  const gameName = (game, short) => game === 'aaa' ? (short ? C.aaa5 : C.avg5) : (data.games[game] || game);

  // ---------- Dated promo: Halloween code prices (disappear after data.hw.ends without a deploy) ----------
  function hwPick() {
    const hw = data.hw; if (!hw || Date.now() > Date.parse(hw.ends)) return null;
    const st = store.get(hw.key);
    if (!st || !st.code || !hw.tiers[st.k]) return null;
    return {code: st.code, k: st.k, tier: hw.tiers[st.k]};
  }
  const pick = hwPick();
  const hwPrice = (e, tier) => tier.t === 'pct' ? Math.floor(e * (1 - tier.v / 100)) : (tier.t === 'amt' && e >= (tier.min || 0) ? Math.floor(e - tier.v) : null);
  const hwOk = g => (data.hw?.ids || []).includes(g.id);
  if (pick) {
    const el = $('[data-hb-hw]', root);
    const off = pick.tier.t === 'pct' ? '−' + pick.tier.v + '%' : '−' + eur(pick.tier.v);
    el.innerHTML = '<span>' + (icons.ticket || '') + esc(fmt(C.hw_strip, {code: pick.code, off})) + '</span><a href="' + esc(data.hw.url) + '" class="icn-host" data-hb-hwlink>' + esc(C.hw_open) + (icons.arrow || '') + '</a>';
    el.hidden = false;
    el.addEventListener('click', e => { if (e.target.closest('[data-hb-hwlink]')) track('home_hw', {home_label: pick.k}); });
  }

  // ---------- Finder ----------
  const finder = $('[data-hb-finder]', root), cards = $('[data-hb-cards]', root), line = $('[data-hb-line]', root);
  if (cards) cards.addEventListener('click', e => {
    const a = e.target.closest('[data-hb-gpu]');
    if (a) track('home_gpu_click', {home_label: a.dataset.hbGpu, value: Number(a.dataset.value) || undefined, currency: 'EUR'});
  });
  const saved = store.get('retech.hv2.find') || {};
  const st = {
    game: saved.game && (saved.game === 'aaa' || data.games[saved.game]) ? saved.game : 'aaa',
    res: RES.includes(saved.res) ? saved.res : (RES.includes(store.get('retech.rs2.res')) ? store.get('retech.rs2.res') : '1440p'),
    fps: [60, 120, 144, 240].includes(saved.fps) ? saved.fps : 60
  };
  const sellable = gpus.filter(g => g.av && g.p);
  let finderPick = null, stageApi = null;
  // Which cards the finder shows for the current filters. Lives here because the stage starts on its pick.
  // In-stock cards that make the target go first, then the cheapest; the cheapest and the best value (FPS per €)
  // are tagged. If nothing makes it, the fastest cards are shown instead.
  function select() {
    const rows = sellable.map(g => ({g, s: score(g, st.game, st.res)})).filter(x => x.s && x.s.v > 0);
    const hit = rows.filter(x => x.s.v >= st.fps);
    let list, tags = {}, cheapest = null;
    if (hit.length) {
      const byPrice = [...hit].sort((a, b) => (a.g.q - b.g.q) || (a.g.S - b.g.S));
      cheapest = [...hit].sort((a, b) => a.g.S - b.g.S)[0];
      const best = [...hit].sort((a, b) => b.s.v / b.g.S - a.s.v / a.g.S)[0];
      list = byPrice.slice(0, data.limit || 6);
      if (!list.includes(best)) list[list.length - 1] = best;
      list.sort((a, b) => a.g.S - b.g.S);
      tags[cheapest.g.h] = C.cheapest; if (best !== cheapest) tags[best.g.h] = C.best;
    } else {
      list = [...rows].sort((a, b) => b.s.v - a.s.v).slice(0, data.limit || 6);
      if (list[0]) tags[list[0].g.h] = C.fastest;
    }
    return {list, tags, hits: hit.length, cheapest};
  }
  const setPick = sel => { finderPick = Object.keys(sel.tags).find(h => sel.tags[h] === C.best) || Object.keys(sel.tags)[0] || finderPick; stageApi?.follow(finderPick); };

  // Sections below the hero are drawn by a module loaded as they approach (rt-bench-find / -ladder / -trade).
  // A tap on one of their buttons before the module arrives is kept and replayed, so nothing is lost.
  function lazy(el, url, margin, start) {
    let mod = null, ready = false, pending = null;
    const load = () => mod || (mod = import(url).then(m => { start(m, () => { const p = pending; pending = null; return p; }); ready = true; }).catch(() => {}));
    el.addEventListener('click', e => { const b = !ready && e.target.closest('button'); if (b) { pending = b; load(); } });
    if (!('IntersectionObserver' in window)) return load();
    const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { io.disconnect(); load(); } }, {rootMargin: margin});
    io.observe(el);
  }
  if (finder && cards && sellable.length) {
    setPick(select());
    finder.hidden = false;
    $('[data-hb-fpsnote]', root).hidden = false;
    // a quarter screen ahead: the finder starts 1.1–2 screens down, so on phones this waits for the first scroll
    lazy($('#hb-find', root), finder.dataset.hbFindModule, '0px 0px 25% 0px', (m, pending) => m.initFinder({root, finder, cards, line, st, select, setPick,
      data, C, L, esc, eur, fmt, gameName, srcText, icons, track, store, calm, pending, hw: {pick, price: hwPrice, ok: hwOk}, tryCard: h => stageApi?.tryCard(h)}));
  }

  // ---------- Doors, links ----------
  root.addEventListener('click', e => {
    const d = e.target.closest('[data-hb-door]'); if (d) track('home_door', {home_label: d.dataset.hbDoor});
    if (e.target.closest('[data-hb-all]')) track('home_all_gpus', {home_label: st.res});
    const p = e.target.closest('[data-hb-preset]'); if (p) track('home_build_open', {home_label: p.dataset.hbPreset});
    const c = e.target.closest('[data-hb-custom]'); if (c) track('home_build_custom', {home_label: c.dataset.hbCustom});
    if (e.target.closest('[data-hb-cfg]')) track('home_build_custom', {home_label: 'configurator'});
    if (e.target.closest('.rt-bd [data-rt-bd-go]')) track('home_build_deposit', {home_label: e.target.closest('.hb-rung')?.querySelector('[data-hb-preset]')?.dataset.hbPreset || '', value: 10, currency: 'EUR'});
    const s = e.target.closest('[data-hb-srv]'); if (s) track('home_srv', {home_label: s.dataset.hbSrv});
    const v = e.target.closest('[data-hb-viber]'); if (v) track('home_viber', {home_label: v.dataset.hbViber});
    if (e.target.closest('[data-hb-call]')) track('home_call');
    const m = e.target.closest('[data-hb-mail]'); if (m) track('home_mail', {home_label: m.dataset.hbMail});
  });
  // the shared footer sits outside the section: its links (id in data-rt-f) and its view are tracked here
  const foot = $('[data-rt-footer]');
  foot?.addEventListener('click', e => { const a = e.target.closest('[data-rt-f]'); if (a) a.dataset.rtF === 'mail' ? track('home_mail', {home_label: 'footer'}) : track('home_footer', {home_label: a.dataset.rtF}); });
  // Tilt follows a fine pointer, max 4° (no tilt on touch or with reduced motion)
  if (!calm && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    $$('[data-hb-tilt]', root).forEach(el => {
      el.addEventListener('pointermove', e => {
        const b = el.getBoundingClientRect(), x = (e.clientX - b.left) / b.width - .5, y = (e.clientY - b.top) / b.height - .5;
        el.classList.add('is-tilting');
        el.style.setProperty('--rx', (-y * 8).toFixed(2) + 'deg'); el.style.setProperty('--ry', (x * 8).toFixed(2) + 'deg');
      });
      el.addEventListener('pointerleave', () => { el.classList.remove('is-tilting'); el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); });
    });
  }

  // ---------- Reveal (once, 14 px, ≤ 50 ms stagger), icon plays on enter, section views ----------
  if ('IntersectionObserver' in window) {
    const items = $$('[data-hb-reveal]', root);
    const inView = el => { const r = el.getBoundingClientRect(); return r.top < innerHeight && r.bottom > 0; };
    items.forEach(el => { if (inView(el)) el.classList.add('is-in'); });
    root.classList.add('is-reveal');
    const rio = new IntersectionObserver(es => {
      let i = 0;
      es.forEach(en => { if (!en.isIntersecting) return; en.target.style.setProperty('--hb-delay', Math.min(i++ * 50, 200) + 'ms'); en.target.classList.add('is-in'); rio.unobserve(en.target); });
    }, {threshold: .15});
    items.filter(el => !el.classList.contains('is-in')).forEach(el => rio.observe(el));
    const iio = new IntersectionObserver(es => es.forEach(en => {
      if (!en.isIntersecting || calm) return;
      const svg = en.target; svg.classList.add('is-play'); iio.unobserve(svg);
      svg.addEventListener('animationend', () => svg.classList.remove('is-play'), {once: true});
    }), {threshold: .6});
    $$('.icn--spin, .icn--turn, .icn--draw, .icn--fill, .icn--flicker, .icn--press', root).forEach(el => iio.observe(el));
    // 40% of the section in view, or 40% of the screen filled by it (the finder is taller than 2.5 phone screens)
    const sio = new IntersectionObserver(es => es.forEach(en => {
      if (en.intersectionRect.height < .4 * Math.min(en.boundingClientRect.height, innerHeight)) return;
      track('home_section', {home_label: en.target.dataset.hbSec}); sio.unobserve(en.target);
    }), {threshold: [.1, .2, .3, .4]});
    $$('[data-hb-sec]', root).forEach(el => sio.observe(el));
    if (foot) { foot.dataset.hbSec = 'footer'; sio.observe(foot); }
  }

  // ---------- Power ladder and trade-in: modules loaded a screen ahead ----------
  const builds = $('#hb-builds', root);
  if (builds) lazy(builds, builds.dataset.hbLadderModule, '100% 0px', (m, pending) => m.initLadder(builds, {root, st, C, L, esc, eur, fmt, score, srcText, track, calm, pending, sceneUrl: builds.dataset.hbSceneModule}));
  const tradeBox = $('[data-hb-trade]', root);
  if (tradeBox) lazy(tradeBox, tradeBox.dataset.hbTradeModule, '100% 0px', (m, pending) => m.initTrade(tradeBox, {data, gpus, C, L, esc, eur, group, store, track, calm, pending}));

  // ---------- No bottom dock: on iOS Safari it sat under the floating toolbar and repeated the hero button ----------
  $('[data-hb-dock]', root)?.remove();
  const hero = $('[data-hb-hero]', root);

  // ---------- Interactive test bench (all devices; HUD and test work without WebGL) ----------
  if (hero) stageApi = initStage(hero, {C, L, st, data, sellable, score, srcText, gameName, eur, esc, fmt, track, icons, calm, pick: () => finderPick});
}

function initStage(hero, x) {
  const {C, st, data, sellable, score, srcText, gameName, eur, esc, fmt, track, icons, calm} = x;
  const $h = s => hero.querySelector(s);
  const stage = $h('[data-gpu-stage]');
  const visual = $h('.hb-hero-visual');
  const ui = {
    name: $h('[data-hb-hud-name]'), meta: $h('[data-hb-hud-meta]'), led: $h('[data-hb-led]'),
    read: $h('[data-hb-hud-read]'), fps: $h('[data-hb-hud-fps]'), sub: $h('[data-hb-hud-sub]'), graph: $h('[data-hb-graph]'),
    load: $h('[data-hb-load]'), loadBar: $h('[data-hb-load] i'), verdict: $h('[data-hb-verdict]'),
    hint: $h('[data-hb-hint]'), cap: $h('[data-hb-caption]'), callouts: $h('[data-hb-callouts]'), legend: $h('[data-hb-legend]'),
    tear: $h('[data-hb-act="tear"]'), test: $h('[data-hb-act="test"]'), thermal: $h('[data-hb-act="thermal"]'),
    prev: $h('[data-hb-act="prev"]'), next: $h('[data-hb-act="next"]')
  };
  if (!stage || !ui.test) return null;
  const touch = matchMedia('(hover: none)').matches;
  ui.hint.textContent = touch ? C.hint_touch : C.hint;

  // the cards you can flip through: everything with FPS data, cheapest first
  const deck = [...sellable].sort((a, b) => a.S - b.S);
  const isAmd = g => /amd|radeon|\brx\s?\d/i.test(g.t + ' ' + g.h);
  const short = t => String(t).replace(/^(NVIDIA\s+GeForce|AMD\s+Radeon|NVIDIA)\s+/i, '');
  let cur = deck.find(g => g.h === x.pick()) || deck[Math.min(3, deck.length - 1)] || null;
  let manual = false;

  // ---- shared 3D state (the motion module's object once it loads; plain object until then) ----
  let S = {p: 0, rx: 0, ry: 0, spin: 0, load: 0, led: 0x24e65a, flip: 0, n: 0, t: 0, thermal: 0};
  let ready3d = false, motion = null;
  const anims = [];
  const tween = (key, to, ms, done) => {
    for (let i = anims.length - 1; i >= 0; i--) if (anims[i].key === key) anims.splice(i, 1);
    anims.push({key, from: S[key], to, t0: performance.now(), ms: calm ? 1 : ms, done});
    wake();
  };
  const setLed = led => {
    const hex = '#' + led.toString(16).padStart(6, '0');
    hero.style.setProperty('--hb-led', hex);
    hero.style.setProperty('--hb-led-glow', led === 0xff4b3e ? 'rgba(255, 75, 62, .2)' : 'rgba(36, 230, 90, .2)');
  };

  function showCard(g, how) {
    if (!g) return;
    const swap = cur && g.h !== cur.h;
    cur = g;
    ui.name.textContent = short(g.t);
    ui.meta.textContent = (g.vram ? g.vram + ' GB · ' : '') + (g.q ? C.from + ' ' : '') + eur(g.S);
    const led = isAmd(g) ? 0xff4b3e : 0x24e65a;
    setLed(led);
    resetTest();
    if (swap && how !== 'silent') {
      // swap: one full turn, the LED colour changes half way
      S.flip = 0; tween('flip', 1, 900, () => { S.flip = 0; });
      setTimeout(() => { S.led = led; wake(); }, calm ? 0 : 420);
      hero.classList.remove('is-swap'); void hero.offsetWidth; hero.classList.add('is-swap');
    } else S.led = led;
    const i = deck.indexOf(g);
    ui.prev.disabled = i <= 0; ui.next.disabled = i >= deck.length - 1;
  }

  // ---- teardown: assembled → apart → paste + pads → assembled, with tappable part callouts ----
  let tearStep = 0, openPart = null;
  const TEAR = [0, 0.12, 0.31];
  const PARTS = [[], ['fans', 'pipes', 'pcie'], ['paste', 'pads', 'vram']];
  // Phones get numbered dots on the card and the names in a row under the controls (labels on the card covered it);
  // the row also says which step of the teardown you're on. Desktop keeps the named pills on the card.
  const narrow = matchMedia('(max-width: 989px)');
  const STEP = x.L === 'en' ? ['', 'Step 1 of 2 · taken apart', 'Step 2 of 2 · new paste and pads'] : ['', 'Стъпка 1 от 2 · разглобена', 'Стъпка 2 от 2 · нова паста и подложки'];
  const parts = document.createElement('div');
  parts.className = 'hb-parts'; parts.hidden = true;
  visual.querySelector('.hb-rail').after(parts);
  function drawCallouts() {
    ui.callouts.innerHTML = '';
    openPart = null;
    parts.hidden = !tearStep;
    if (!tearStep) return;
    parts.innerHTML = '<p class="hb-parts-step">' + esc(STEP[tearStep]) + '</p><div class="hb-parts-list">' +
      PARTS[tearStep].map((id, i) => '<button type="button" class="hb-part" data-part="' + id + '" aria-pressed="false"><i>' + (i + 1) + '</i>' + esc(C.parts[id][0]) + '</button>').join('') + '</div>';
    if (!ready3d) return;
    PARTS[tearStep].forEach((id, i) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'hb-callout'; b.dataset.part = id; b.setAttribute('aria-expanded', 'false');
      b.innerHTML = '<i class="hb-callout-n" aria-hidden="true">' + (i + 1) + '</i><span class="hb-callout-t">' + esc(C.parts[id][0]) + '</span>';
      ui.callouts.append(b);
    });
    placeCallouts();
  }
  function selectPart(id) {
    const open = id && openPart !== id;
    ui.callouts.querySelectorAll('.hb-callout').forEach(o => { o.setAttribute('aria-expanded', String(open && o.dataset.part === id)); o.querySelector('.hb-callout-tip')?.remove(); });
    parts.querySelectorAll('.hb-part').forEach(o => o.setAttribute('aria-pressed', String(open && o.dataset.part === id)));
    openPart = open ? id : null;
    if (open) {
      if (narrow.matches) ui.cap.textContent = C.parts[id][1];
      else {
        const b = ui.callouts.querySelector('[data-part="' + id + '"]');
        if (b) { const tip = document.createElement('span'); tip.className = 'hb-callout-tip'; tip.textContent = C.parts[id][1]; b.append(tip); }
      }
      track('home_part', {home_label: id});
    } else if (narrow.matches) ui.cap.textContent = [C.cap0, C.cap1, C.cap2][tearStep] || '';
  }
  parts.addEventListener('click', e => { const b = e.target.closest('.hb-part'); if (b) selectPart(b.dataset.part); });
  function placeCallouts() {
    if (!motion?.partAnchor || !ui.callouts.firstChild) return;
    const r = stage.getBoundingClientRect();
    ui.callouts.querySelectorAll('.hb-callout').forEach(b => {
      const a = motion.partAnchor(b.dataset.part, r.width / r.height);
      if (!a) return;
      // the dot sits on the part; labels on the right half grow leftwards so they stay inside the stage
      b.classList.toggle('is-left', a.x > .55);
      b.style.left = (Math.min(.96, Math.max(.04, a.x)) * 100).toFixed(2) + '%';
      b.style.top = (Math.min(.82, Math.max(.2, a.y)) * 100).toFixed(2) + '%';
    });
  }
  ui.callouts.addEventListener('click', e => { const b = e.target.closest('.hb-callout'); if (b) selectPart(b.dataset.part); });
  function setTear(step, user) {
    tearStep = step;
    // a finished test's readout, bar and verdict would sit on the card and push the page down
    if (step) resetTest();
    if (step) { tween('rx', 0, 500); tween('ry', Math.round(S.ry / (Math.PI * 2)) * Math.PI * 2, 500); }
    ui.callouts.innerHTML = ''; parts.hidden = true;
    tween('p', TEAR[step] ?? 0, step === 2 ? 1700 : 1100, () => { drawCallouts(); });
    ui.tear.querySelector('span').textContent = [C.tear0, C.tear1, C.tear2][step];
    // phones skip the "reassembled" line: it pushed the main button under Safari's toolbar
    ui.cap.textContent = step === 0 ? (user && !narrow.matches ? C.cap3 : (S.thermal > .5 ? C.thermal_on : '')) : [C.cap0, C.cap1, C.cap2][step];
    hero.dataset.tear = String(step);
    ui.test.disabled = step !== 0;
    if (user) track('home_3d', {home_label: 'tear' + step});
  }
  ui.tear.addEventListener('click', () => { stopTest(); setTear((tearStep + 1) % 3, true); });

  // ---- thermal view (illustration): the motion module swaps the card's materials for a heat shader ----
  // The shader is fetched the first time it's wanted (hover, focus or click); the cross-fade holds until it's in.
  let thermalOn = false;
  const shader = () => motion?.loadThermal ? motion.loadThermal() : Promise.resolve();
  function setThermal(on, user) {
    thermalOn = on;
    ui.thermal.setAttribute('aria-pressed', String(on));
    hero.classList.toggle('is-thermal', on);
    ui.legend.hidden = !on;
    stage.classList.add('is-swapping');
    const ready = on ? shader() : Promise.resolve();
    setTimeout(() => ready.then(() => { S.thermal = thermalOn ? 1 : 0; stage.classList.remove('is-swapping'); wake(); }), calm ? 0 : 220);
    if (!tearStep) ui.cap.textContent = on ? C.thermal_on : '';
    if (user) track('home_thermal', {home_label: on ? 'on' : 'off'});
  }
  ui.thermal.addEventListener('click', () => setThermal(!thermalOn, true));
  ['pointerenter', 'focus'].forEach(t => ui.thermal.addEventListener(t, shader, {once: true}));

  // ---- stress test with a frame-time graph drawn from the average and the 1% low ----
  let test = null;
  const g2d = ui.graph?.getContext('2d');
  function resetTest() { stopTest(true); ui.verdict.hidden = true; ui.read.hidden = true; ui.load.hidden = true; hero.classList.remove('is-pass', 'is-passing'); }
  function stopTest(silent) {
    if (!test) return;
    test = null; hero.classList.remove('is-testing');
    tween('load', 0, 900);
    ui.test.querySelector('span').textContent = C.test; ui.test.setAttribute('aria-pressed', 'false');
    if (!silent) ui.verdict.hidden = false;
  }
  function startTest(src) {
    if (!cur) return;
    if (tearStep) setTear(0, false);
    const sc = score(cur, st.game, st.res);
    test = {t0: performance.now(), sc, samples: [], lastSample: 0, done: false};
    hero.classList.add('is-testing'); hero.classList.remove('is-pass', 'is-passing');
    ui.verdict.hidden = true; ui.read.hidden = false; ui.load.hidden = false;
    ui.sub.textContent = fmt(C.run, {src: sc ? srcText(sc.src) : '—', game: gameName(st.game, true), res: st.res});
    ui.test.querySelector('span').textContent = C.testing; ui.test.setAttribute('aria-pressed', 'true');
    tween('load', 1, 1400);
    track('home_3d', {home_label: 'test:' + cur.h + (src ? ':' + src : '')});
    wake();
  }
  function drawGraph() {
    if (!g2d || !test?.sc) return;
    const w = ui.graph.width, h = ui.graph.height, s = test.samples;
    const avgMs = 1000 / test.sc.v, lowMs = test.sc.low ? 1000 / test.sc.low : avgMs * 1.25, tgtMs = 1000 / st.fps;
    const max = Math.max(lowMs, tgtMs) * 1.25;
    const y = ms => h - 2 - (ms / max) * (h - 4);
    g2d.clearRect(0, 0, w, h);
    g2d.strokeStyle = 'rgba(238, 243, 240, .35)'; g2d.setLineDash([3, 3]); g2d.lineWidth = 1;
    g2d.beginPath(); g2d.moveTo(0, y(tgtMs)); g2d.lineTo(w, y(tgtMs)); g2d.stroke(); g2d.setLineDash([]);
    g2d.strokeStyle = hero.classList.contains('is-pass') ? '#5bffb0' : '#ffcf6b'; g2d.lineWidth = 1.5;
    g2d.beginPath();
    s.forEach((ms, i) => { const px = w - (s.length - 1 - i) * 3; i ? g2d.lineTo(px, y(ms)) : g2d.moveTo(px, y(ms)); });
    g2d.stroke();
  }
  function finishTest() {
    const t = test; if (!t) return;
    t.done = true;
    const fps = t.sc?.v || 0;
    const ok = fps >= st.fps;
    const i = deck.indexOf(cur);
    const stronger = ok ? null : deck.slice(i + 1).find(g => (score(g, st.game, st.res)?.v || 0) >= st.fps) || deck[i + 1];
    ui.verdict.className = 'hb-verdict ' + (ok ? 'is-ok' : 'is-low');
    ui.verdict.innerHTML = !fps ? esc(C.nodata) :
      '<b>' + esc(fmt(ok ? C.pass : C.fail, {fps: (t.sc.src === 'est' ? '~' : '') + fps, target: st.fps})) + '</b>' +
      (ok ? ' <a href="' + esc(cur.u) + '" class="icn-host" data-hb-see>' + esc(C.see) + (icons.arrow || '') + '</a>'
          : (stronger ? ' <button type="button" class="icn-host" data-hb-up="' + esc(stronger.h) + '">' + esc(C.up) + (icons.arrow || '') + '</button>' : '')) +
      '<small>' + esc(fmt(C.basis, {src: srcText(t.sc.src), game: gameName(st.game), res: st.res})) + '</small>';
    if (ok && fps) { hero.classList.add('is-pass', 'is-passing'); setTimeout(() => hero.classList.remove('is-passing'), 1700); drawGraph(); }
    stopTest();
    // the verdict under the card carries the number; on phones the readout would keep covering the card
    ui.load.hidden = true;
    if (narrow.matches) setTimeout(() => { if (!test) ui.read.hidden = true; }, calm ? 0 : 1800);
    if (ok && navigator.vibrate) try { navigator.vibrate(18); } catch {}
    track('home_3d', {home_label: (ok ? 'pass:' : 'low:') + cur.h, value: fps});
  }
  ui.test.addEventListener('click', () => { if (test) stopTest(); else startTest(); });
  ui.verdict.addEventListener('click', e => {
    const up = e.target.closest('[data-hb-up]');
    if (up) { manual = true; showCard(deck.find(g => g.h === up.dataset.hbUp)); setTimeout(() => startTest('up'), calm ? 0 : 900); }
    if (e.target.closest('[data-hb-see]')) track('home_3d', {home_label: 'see:' + cur.h});
  });
  const step = (d, how) => { const i = deck.indexOf(cur) + d; if (deck[i]) { manual = true; showCard(deck[i]); track('home_3d', {home_label: 'swap'}); } };
  ui.prev.addEventListener('click', () => step(-1));
  ui.next.addEventListener('click', () => step(1));

  // ---- keyboard: only while focus is inside the bench, so single letters never hijack typing ----
  const keysUsed = new Set();
  const keyTrack = label => { if (!keysUsed.has(label)) { keysUsed.add(label); track('home_key', {home_label: label}); } };
  visual.addEventListener('keydown', e => {
    if (e.altKey || e.ctrlKey || e.metaKey || e.target.closest('input, select, textarea')) return;
    const k = e.key;
    if (k === 'ArrowLeft' || k === 'ArrowRight') { e.preventDefault(); tween('ry', S.ry + (k === 'ArrowLeft' ? -.35 : .35), 240); idleAt = performance.now() + 4000; keyTrack('rotate'); }
    else if (k === 'ArrowUp' || k === 'ArrowDown') { e.preventDefault(); tween('rx', Math.max(-.7, Math.min(.7, S.rx + (k === 'ArrowUp' ? -.2 : .2))), 240); idleAt = performance.now() + 4000; keyTrack('rotate'); }
    else if (k === 't' || k === 'T') { ui.tear.click(); keyTrack('tear'); }
    else if ((k === 's' || k === 'S') && !ui.test.disabled) { ui.test.click(); keyTrack('test'); }
    else if (k === 'h' || k === 'H') { ui.thermal.click(); keyTrack('thermal'); }
    else if (k === '[' && !ui.prev.disabled) { ui.prev.click(); keyTrack('prev'); }
    else if (k === ']' && !ui.next.disabled) { ui.next.click(); keyTrack('next'); }
  });

  // ---- drag / swipe to turn, with inertia; springs back to the hero pose after 4 s idle ----
  let drag = null, idleAt = 0, dragged = false, vy = 0, vx = 0;
  stage.addEventListener('pointerdown', e => {
    // presses on callouts and other controls are clicks, not drags (pointer capture would swallow the click)
    if (e.button > 0 || e.target.closest('button, a')) return;
    drag = {x: e.clientX, y: e.clientY, rx: S.rx, ry: S.ry, id: e.pointerId, on: e.pointerType === 'mouse', lx: e.clientX, ly: e.clientY, lt: performance.now()};
    vx = vy = 0;
    if (drag.on) stage.setPointerCapture(e.pointerId);
  });
  stage.addEventListener('pointermove', e => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    if (!drag.on) {
      // touch: only claim clearly horizontal swipes so the page still scrolls
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      drag.on = true; try { stage.setPointerCapture(e.pointerId); } catch {}
    }
    const k = e.pointerType === 'mouse' ? 0.012 : 0.009, nowT = performance.now(), dt = Math.max(1, nowT - drag.lt);
    S.ry = drag.ry + dx * k;
    if (e.pointerType === 'mouse') S.rx = Math.max(-0.7, Math.min(0.7, drag.rx + dy * 0.008));
    vy = (e.clientX - drag.lx) * k / dt; vx = e.pointerType === 'mouse' ? (e.clientY - drag.ly) * 0.008 / dt : 0;
    drag.lx = e.clientX; drag.ly = e.clientY; drag.lt = nowT;
    idleAt = nowT + 4000; hero.classList.add('is-dragged');
    if (!dragged) { dragged = true; track('home_3d', {home_label: 'drag'}); }
    wake();
  });
  const end = e => { if (drag && e.pointerId === drag.id) { if (performance.now() - drag.lt > 80) vx = vy = 0; drag = null; idleAt = performance.now() + 4000; wake(); } };
  stage.addEventListener('pointerup', end); stage.addEventListener('pointercancel', end);

  // ---- frame loop: only while the bench is on screen; idle frames capped at 30 fps ----
  let visible = true, raf = 0, last = performance.now(), lastKick = 0;
  new IntersectionObserver(es => { visible = es[0].isIntersecting; if (visible) wake(); }, {rootMargin: '80px'}).observe(stage);
  document.addEventListener('visibilitychange', wake);
  function wake() { if (!raf && visible && !document.hidden) { last = performance.now(); raf = requestAnimationFrame(loop); } }
  const kick = () => hero.dispatchEvent(new Event('rt-gpu:render'));
  let aspect = 1.6;
  const measure = () => { const r = stage.getBoundingClientRect(); if (r.width && r.height) { aspect = r.width / r.height; S.aspect = aspect; wake(); } };
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(stage); else measure();
  function loop(now) {
    raf = 0;
    if (!visible || document.hidden) return;
    S.aspect = aspect;
    const dtMs = Math.min(50, now - last), dt = dtMs / 1000; last = now;
    S.t += dt;
    for (let i = anims.length - 1; i >= 0; i--) {
      const a = anims[i], k = Math.min(1, (now - a.t0) / a.ms), e = k < .5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
      S[a.key] = a.from + (a.to - a.from) * e;
      if (k >= 1) { anims.splice(i, 1); a.done?.(); }
    }
    // inertia after a flick
    if (!drag && (Math.abs(vy) > 1e-5 || Math.abs(vx) > 1e-5)) {
      S.ry += vy * dtMs; S.rx = Math.max(-0.7, Math.min(0.7, S.rx + vx * dtMs));
      const decay = Math.pow(0.92, dtMs / 16.7); vy *= decay; vx *= decay;
      if (Math.abs(vy) < 1e-5) vy = 0; if (Math.abs(vx) < 1e-5) vx = 0;
    }
    const busy = anims.length || drag || test || vy || vx || S.load > 0.01;
    // idle: spring back to the hero pose with a gentle sway (the only idle animation on the page)
    if (!drag && !tearStep && now > idleAt && !calm && !anims.some(a => a.key === 'flip')) {
      const home = Math.round(S.ry / (Math.PI * 2)) * Math.PI * 2 + Math.sin(S.t * 0.6) * 0.35;
      S.ry += (home - S.ry) * Math.min(1, 0.04 * dtMs / 16.7);
      S.rx += (0 - S.rx) * Math.min(1, 0.04 * dtMs / 16.7);
    }
    S.spin += dt * (1.2 + S.load * 26);
    if (test && !test.done) {
      const k = Math.min(1, (now - test.t0) / 3600), target = test.sc?.v || 0;
      const shown = Math.round(target * Math.min(1, k * 1.25) * (0.97 + Math.sin(S.t * 13) * 0.03 * (k < 1 ? 1 : 0)));
      ui.fps.textContent = target ? String(k >= 1 ? target : shown) : '—';
      ui.loadBar.style.transform = 'scaleX(' + Math.min(1, k * 1.6).toFixed(3) + ')';
      hero.style.setProperty('--hb-load', S.load.toFixed(3));
      if (test.sc && now - test.lastSample > 33) {
        // frame-time illustration: the average with ±4% jitter and occasional dips to the 1% low
        test.lastSample = now;
        const base = 1000 / test.sc.v, low = test.sc.low ? 1000 / test.sc.low : base * 1.25;
        const ms = Math.random() < 0.05 ? low * (0.95 + Math.random() * 0.1) : base * (1 + (Math.random() - 0.5) * 0.08) * (k < .3 ? 1.6 - k * 2 : 1);
        test.samples.push(ms); if (test.samples.length > 60) test.samples.shift();
        drawGraph();
      }
      if (k >= 1) finishTest();
    }
    if (tearStep && ui.callouts.firstChild) placeCallouts();
    if (ready3d && (busy || now - lastKick >= 33)) { S.n++; lastKick = now; kick(); }
    if (ready3d || busy) raf = requestAnimationFrame(loop);
  }

  // ---- load the 3D model after load + idle (poster stays if WebGL, motion or data saving rule it out) ----
  const start = async () => {
    try {
      motion = await import(hero.dataset.motionModule);
      motion.configure({thermalModule: hero.dataset.thermalModule});
      Object.assign(motion.state, S); S = motion.state;
      const gpu = await import(hero.dataset.gpuModule);
      await gpu.mountGPU(hero);
      ready3d = hero.classList.contains('is-3d-ready');
      if (!ready3d) return;
      track('home_3d', {home_label: 'ready'});
      // entrance: drop in with a turn and a quick fan spin-up
      S.ry = -1.4; S.rx = 0.25; tween('ry', 0, calm ? 1 : 1600); tween('rx', 0, calm ? 1 : 1600);
      wake();
    } catch { /* poster stays */ }
  };
  hero.classList.add('is-ui');
  if (hero.dataset['3d'] !== 'false' && new URLSearchParams(location.search).get('rt_3d') !== '0') {
    const go = () => (window.requestIdleCallback || setTimeout)(start, {timeout: 1500});
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, {once: true});
  }
  setTear(0, false);
  showCard(cur, 'silent');
  wake();

  return {
    follow(h) { if (!manual && !test) { const g = deck.find(c => c.h === h); if (g && g !== cur) showCard(g, 'silent'); } },
    tryCard(h) {
      const g = deck.find(c => c.h === h); if (!g) return;
      manual = true;
      hero.scrollIntoView({behavior: calm ? 'auto' : 'smooth', block: 'center'});
      track('home_3d', {home_label: 'try:' + h});
      setTimeout(() => { showCard(g); setTimeout(() => startTest('card'), calm ? 0 : 900); }, calm ? 0 : 450);
    }
  };
}
